import { Group, Stack, Text, Tooltip } from '@mantine/core';

import RiskIndicators from '../../riskindicators/RiskIndicators';
import RouterLink from '@/components/content/link/RouterLink';

import { createHelper } from '../../issue/utils/helper';
import { formatIsoDate } from '@/utils/date';
import { getEntries } from '@/utils';

import type { Module } from '../../issue/types/modulesv2';
import type { SignalCollectors } from '../types';
import type { NormalizedSignal } from '@/services/unifiedapi';

import AiIcon from '@/assets/icons/content/ai.svg';
import TranslatedIcon from '@/assets/icons/content/translated.svg';

import classes from '@/client/features/issue/components/IssueModule.module.css';

const helper = createHelper<NormalizedSignal>();

export const signalSourceDrawer = (
  collector: SignalCollectors,
  title: string = 'Mention in Risky Source'
): Array<Module<NormalizedSignal>> => [
  {
    submodules: [
      helper.accessor((data) => data.title, {
        element: ({ getValue }) => (
          <Stack gap={10} align="center" justify="center">
            <Text fw="bold" ta="center">
              {getValue()}
            </Text>
            <Text size="sm">{title}</Text>
          </Stack>
        ),
      }),
    ],
  },
  {
    submodules: [
      helper.accessor(
        (data) => ({
          locations: getEntries(data.catalog.locations).map(([id, name]) => ({ id, name })),
          technologies: getEntries(data.catalog.technologies).map(([id, name]) => ({ id, name })),
          industry_sectors: getEntries(data.catalog.industry_sectors).map(([id, name]) => ({
            id,
            name,
          })),
        }),
        {
          title: 'Your Relevancy',
          element: RiskIndicators,
          scroll: false,
        }
      ),
    ],
  },
  {
    submodules: [
      helper.accessor(
        (data) =>
          data.ai_summary || data.summary
            ? {
                ai: data.ai_summary,
                summary: data.summary,
              }
            : null,
        {
          element: ({ getValue }) => {
            const { ai, summary } = getValue();

            return (
              <>
                <h3 className={classes.submoduleTitle}>
                  <Group gap={5} component="span">
                    {ai ? 'AI' : ''} Summary
                    {ai && <AiIcon />}
                  </Group>
                </h3>

                {ai || summary}
              </>
            );
          },
        }
      ),
    ],
  },
  {
    hide: collector !== 'forum-posts' && collector !== 'telegram',
    submodules: [
      helper.accessor(
        (data) =>
          data.text || data.translated_text
            ? {
                language: data.language,
                content: data.text,
                translated: data.translated_text,
              }
            : null,
        {
          scroll: false,
          element: ({ getValue }) => {
            const { language, content, translated } = getValue();
            const isEnglish = language === 'en';

            return (
              <>
                <h3 className={classes.submoduleTitle}>
                  <Group gap={5} component="span">
                    {!isEnglish && translated ? 'Translated' : ''} Content
                    {!isEnglish && (
                      <Tooltip label={language} disabled={!language}>
                        <TranslatedIcon />
                      </Tooltip>
                    )}
                  </Group>
                </h3>

                {!isEnglish && translated ? translated : content}
              </>
            );
          },
        }
      ),
    ],
  },
  {
    title: 'Context Information',
    submodules: [
      helper.accessor(
        (data) =>
          data.catalog.source && data.catalog.source.length
            ? { names: data.catalog.source, ids: data.catalog.source_id }
            : null,
        {
          title: 'Source',
          element: ({ getValue }) => {
            const { names, ids } = getValue();

            return (Array.isArray(names) ? names : []).flatMap((source: string, i: number) => {
              const link = (
                <RouterLink key={source} to={`/catalogs/sources/entity/${ids[i]}`}>
                  {source}
                </RouterLink>
              );

              return i < names.length - 1 ? [link, ', '] : [link];
            });
          },
        }
      ),
      helper.accessor((data) => data.link, {
        title: 'URL',
      }),
      helper.accessor((data) => data.author.forum, {
        title: 'Author',
        hide: collector !== 'forum-posts',
      }),
      helper.accessor((data) => data.author.telegram, {
        title: 'Author',
        hide: collector !== 'telegram',
      }),
      helper.accessor((data) => data.author.jobtitle, {
        title: 'Author Title',
        hide: collector !== 'forum-posts',
      }),
      helper.accessor((data) => data.thread.title, {
        title: 'Thread Title',
        hide: collector !== 'forum-posts',
      }),
      helper.accessor((data) => data.channel.title, {
        title: 'Thread Title',
        hide: collector !== 'telegram',
      }),
      helper.accessor((data) => data.thread.path, {
        title: 'Thread Path',
        hide: collector !== 'forum-posts',
      }),
      helper.accessor((data) => data.published_at, {
        title: 'Published At',
        element: ({ getValue }) => formatIsoDate(getValue()),
      }),
    ],
  },
];
